/**
 * @license
 *
 * Copyright IBM Corp. 2021, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
// @ts-nocheck

import { html } from 'lit';
import { property } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import {
  formatVideoCaption,
  formatVideoDuration,
} from '@carbon/ibmdotcom-utilities/es/utilities/formatVideoCaption/formatVideoCaption.js';
import C4DCardLink from '../card-link/card-link';
import '../card-link/card-link-heading';
import CTAMixin from '../../component-mixins/cta/cta-v1';
import VideoCTAMixin from '../../component-mixins/cta/video';
import C4DCardCTAFooter from './card-cta-footer';
import './card-cta-footer';
import { CTA_TYPE } from './defs';
import styles from './cta.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

export { CTA_TYPE };

const { stablePrefix: c4dPrefix } = settings;

/**
 * Card CTA.
 *
 * @element c4d-card-cta
 * @csspart heading - The heading content. Usage: `c4d-card-link-cta::part(heading)`
 */
@customElement(`${c4dPrefix}-card-link-cta`)
class C4DCardLinkCTA extends VideoCTAMixin(CTAMixin(C4DCardLink)) {
  protected _renderHeading() {
    const {
      ctaType,
      videoName,
      formatVideoCaption: formatVideoCaptionInEffect,
    } = this;
    if (ctaType !== CTA_TYPE.VIDEO) {
      return super._renderHeading();
    }
    const caption = formatVideoCaptionInEffect({ name: videoName });
    return html`
      <slot name="heading"></slot
      ><c4d-card-link-heading part="heading">${caption}</c4d-card-link-heading>
    `;
  }

  /**
   * The CTA type.
   */
  @property({ reflect: true, attribute: 'cta-type' })
  ctaType = CTA_TYPE.REGULAR;

  /**
   * The formatter for the video caption, composed with the video name and the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  @property({ attribute: false })
  formatVideoCaption = formatVideoCaption;

  /**
   * The formatter for the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  @property({ attribute: false })
  formatVideoDuration?: typeof formatVideoDuration;

  /**
   * The video duration.
   */
  @property({ type: Number, attribute: 'video-duration' })
  videoDuration?: number;

  /**
   * The video name.
   */
  @property({ attribute: 'video-name' })
  videoName?: string;

  /**
   * The video custom description.
   */
  @property({ attribute: 'video-description' })
  videoDescription?: string;

  /**
   * The video thumbnail URL.
   */
  @property({ attribute: 'video-thumbnail-url' })
  videoThumbnailUrl?: string;

  updated(changedProperties) {
    super.updated(changedProperties);
    const footer = this.querySelector(
      (this.constructor as typeof C4DCardLinkCTA).selectorFooter
    );
    if (
      changedProperties.has('ctaType') ||
      changedProperties.has('formatCaption') ||
      changedProperties.has('formatDuration') ||
      changedProperties.has('videoDuration') ||
      changedProperties.has('videoName')
    ) {
      const {
        ctaType,
        videoDuration,
        videoName,
        videoDescription,
        formatVideoCaption: formatVideoCaptionInEffect,
        formatVideoDuration: formatVideoDurationInEffect,
      } = this;
      const headingText = this.querySelector(
        `${c4dPrefix}-card-link-heading`
      )?.textContent;
      const copyText = this.textContent;
      if (footer) {
        (footer as C4DCardCTAFooter).altAriaLabel =
          videoName || headingText || copyText;
        (footer as C4DCardCTAFooter).ctaType = ctaType;
        (footer as C4DCardCTAFooter).videoDuration = videoDuration;
        (footer as C4DCardCTAFooter).videoName = videoName;
        (footer as C4DCardCTAFooter).videoDescription = videoDescription;
        if (formatVideoCaptionInEffect) {
          (footer as C4DCardCTAFooter).formatVideoCaption =
            formatVideoCaptionInEffect;
        }
        if (formatVideoDurationInEffect) {
          (footer as C4DCardCTAFooter).formatVideoDuration =
            formatVideoDurationInEffect;
        }
      }
    }
  }

  static get stableSelector() {
    return `${c4dPrefix}--card-link-cta`;
  }

  /**
   * A selector that will return the child footer.
   */
  static get selectorFooter() {
    return `${c4dPrefix}-card-cta-footer`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DCardLinkCTA;
